import { useState, useEffect, useCallback } from "react";
import { withAppHOC } from "../../hoc";
import {
  StyledContentContainer,
  StyledMainAreaContainer,
  StyledButtonAsLink,
  StyledAuthLinks,
  StyledPara,
  StyledCallbackUrl,
  StyledTextHighlight,
} from "../../components/styled";
import Loader from "../../components/Loader/Loader";
import PageHeader from "../../components/PageHeader/PageHeader";
import {
  Form,
  Fieldset,
  Label,
  TextInput,
  OTPInput,
  MessageBox,
  Button,
} from "../../components/FormElements";
import API from "../../api";
import { OTP_VERIFICATION_TYPES, RESPONSE_STATUSES } from "../../constants";
import { getAuthData } from "../../utils";

const APISettings = () => {
  const [state, setState] = useState({
    isLoading: false,
    responseStatus: "",
    responseMessage: "",
    routeStage: "Request",
    oneTimePassword: "",
    requestToken: "",
    requestIpAddress: "",
    callbackUrl: "",
  });
  const { userProfile } = getAuthData();

  const readData = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: "",
      responseMessage: "",
    }));
    try {
      const response = await API.get(`/buyer/api-settings`);
      const { status, message, data } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        setState((prevState) => ({
          ...prevState,
          requestIpAddress: data?.ipAddress,
          callbackUrl: data?.callbackUrl,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
        }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: "",
      responseMessage: "",
    }));
    try {
      let response;
      if (state.routeStage === "Request") {
        response = await API.put(`/buyer/api-settings`, {
          requestIpAddress: state.requestIpAddress,
          callbackUrl: state.callbackUrl,
        });
      } else {
        response = await API.post(`/authentication/verify-otp`, {
          mobileNumber: userProfile.mobileNumber,
          requestType: OTP_VERIFICATION_TYPES.UPDATE_API_SETTINGS,
          requestToken: state.requestToken,
          oneTimePassword: state.oneTimePassword,
          requestIpAddress: state.requestIpAddress,
          callbackUrl: state.callbackUrl,
        });
      }
      const { status, message, token } = response.data;
      if (status === RESPONSE_STATUSES.SUCCESS) {
        if (state.routeStage === "Request") {
          setState((prevState) => ({
            ...prevState,
            routeStage: "OTP",
            requestToken: token,
          }));
        } else {
          setState((prevState) => ({
            ...prevState,
            responseStatus: RESPONSE_STATUSES.SUCCESS,
            responseMessage: message,
            routeStage: "Request",
            requestToken: "",
            oneTimePassword: "",
          }));
          setTimeout(() => {
            setState((prevState) => ({
              ...prevState,
              responseStatus: "",
              responseMessage: "",
            }));
          }, 3000);
        }
      } else {
        setState((prevState) => ({
          ...prevState,
          responseStatus: RESPONSE_STATUSES.FAILED,
          responseMessage: message,
          requestToken: token,
        }));
      }
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  const resendOTP = async (e) => {
    e.preventDefault();
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      responseStatus: "",
      responseMessage: "",
    }));
    try {
      const response = await API.post("/authentication/resend-otp", {
        requestToken: state.requestToken,
        mobileNumber: userProfile?.mobileNumber,
      });
      const { status, message, token } = response.data;
      setState((prevState) => ({
        ...prevState,
        responseStatus: status,
        responseMessage: message,
        requestToken: token,
      }));
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        responseStatus: RESPONSE_STATUSES.FAILED,
        responseMessage: error.message,
      }));
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  };

  useEffect(() => {
    readData();
  }, [readData]);

  const renderPageHeading = () => <PageHeader title="API Settings" />;

  return (
    <>
      <Loader isLoading={state.isLoading} />
      {renderPageHeading()}
      <StyledContentContainer>
        <StyledMainAreaContainer>
          <Form
            action="#"
            method="POST"
            onSubmit={onSubmit}
            style={{ maxWidth: "500px" }}
          >
            <Fieldset>
              <Label required>Server IP Address</Label>
              <TextInput
                value={state.requestIpAddress}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    requestIpAddress: value,
                  }))
                }
                placeholder="Server IP Address"
                disabled={state.routeStage === "OTP" || state.isLoading}
              />
            </Fieldset>
            <Fieldset>
              <Label>Callback URL</Label>
              <TextInput
                value={state.callbackUrl}
                onChange={(value) =>
                  setState((prevState) => ({
                    ...prevState,
                    callbackUrl: value,
                  }))
                }
                placeholder="Callback URL"
                disabled={state.routeStage === "OTP" || state.isLoading}
              />
            </Fieldset>
            <Fieldset>
              <StyledCallbackUrl>
                {`Example : ${
                  state.callbackUrl
                    ? state.callbackUrl
                    : `https://www.yourdomain.com/recharge_callback`
                }?status=[TRANSACTION_STATUS]&txn_id=[TRANSACTION_ID]&operators_id=[OPERATOR_ID]&customer_account_number=[CUSTOMER_ACCOUNT_NUMBER]&customer_mobile=[CUSTOMER_MOBILE_NUMBER]&amount=[AMOUNT]&payment_time=[PAYMENT_TIME]&ref_number=[YOUR_REF_NUMBER]`}
              </StyledCallbackUrl>
            </Fieldset>
            {state.routeStage === "OTP" && (
              <>
                <Fieldset>
                  <StyledPara>
                    Please enter your OTP sent to{" "}
                    <StyledTextHighlight>
                      {userProfile?.mobileNumber}
                    </StyledTextHighlight>
                  </StyledPara>
                </Fieldset>
                <Fieldset>
                  <OTPInput
                    value={state.oneTimePassword}
                    onChange={(value) =>
                      setState((prevState) => ({
                        ...prevState,
                        oneTimePassword: value,
                      }))
                    }
                    placeholder="OTP"
                    disabled={state.isLoading}
                  />
                </Fieldset>
              </>
            )}
            {state.responseStatus && state.responseMessage && (
              <Fieldset>
                <MessageBox
                  status={state.responseStatus}
                  message={state.responseMessage}
                />
              </Fieldset>
            )}
            <Fieldset>
              <Button disabled={state.isLoading} type="submit">
                {state.routeStage === "OTP" ? "Verify & Proceed" : "Submit"}
              </Button>
            </Fieldset>
            {state.routeStage === "OTP" && (
              <Fieldset>
                <StyledAuthLinks>
                  <StyledButtonAsLink type="button" onClick={resendOTP}>
                    Resend OTP
                  </StyledButtonAsLink>
                </StyledAuthLinks>
              </Fieldset>
            )}
          </Form>
        </StyledMainAreaContainer>
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(APISettings);
